import React, { useState, useRef } from "react";
import { FaStar } from "react-icons/fa";
import Loader from "../../../components/Loader"
import ThemedTable from "../../../components/ThemedTable";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
/* ================= COMMON STYLES ================= */
const inputClass =
  "w-full border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition";

const labelClass = "block text-xs font-medium text-slate-700";

const fileInputClass =
  "block w-full text-sm text-slate-600 file:mr-4 file:py-2 file:px-4 " +
  "file:rounded-md file:border-0 file:text-sm file:font-medium " +
  "file:bg-amber-50 file:text-amber-700 hover:file:bg-amber-100";

/* ================= COMPONENT ================= */
const AddProducts = ({
  productData,
  setProductData,
  brands,
  categories,
  units,
  setUnits,
  thumbnailPreview,
  locations,
  setShowLocationModal,
  imagesPreview,
  handleThumbnail,
  handleImages,
  removeImage,
  onClose,
  isEdit,
  onSubmit,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showOtherGst, setShowOtherGst] = useState(false);
  const fileInputRef = useRef(null);
   const normalizedProductData = {
    ...productData,
    warehouses:
      Array.isArray(productData?.warehouses) && productData.warehouses.length > 0
        ? productData.warehouses
        : [{ locationId: "", quantity: 0, expiryDate: "", batchId: "" }],
  };

  /* ================= FIX: HELPERS ================= */
 const updateWarehouse = (index, field, value) => {
  const updated = [...(productData.warehouses || [])];
  updated[index] = {
    ...updated[index],
    [field]: value,
  };

  setProductData((prev) => ({
    ...prev,
    warehouses: updated,
  }));
};

 const addWarehouse = () => {
  setProductData((prev) => ({
    ...prev,
    warehouses: [
      ...(prev.warehouses && prev.warehouses.length
        ? prev.warehouses
        : [{ locationId: "", quantity: 0, expiryDate: "", batchId: "" }]),
      { locationId: "", quantity: 0, expiryDate: "", batchId: "" },
    ],
  }));
};

const removeWarehouse = (index) => {
  setProductData((prev) => {
    const updated = (prev.warehouses || []).filter((_, i) => i !== index);

    return {
      ...prev,
      warehouses: updated.length
        ? updated
        : [{ locationId: "", quantity: 0, expiryDate: "", batchId: "" }],
    };
  });
};

  const gstRate = parseFloat(productData.taxRate) || 0;
  const isTaxIncluded = productData.taxInclude !== false;

  const calculateTaxValues = (val) => {
    const numericVal = parseFloat(val) || 0;
    if (numericVal <= 0) return { base: 0, gst: 0, total: 0 };
    if (isTaxIncluded) {
      const base = numericVal / (1 + gstRate / 100);
      const gst = numericVal - base;
      return { base, gst, total: numericVal };
    } else {
      const gst = numericVal * (gstRate / 100);
      return { base: numericVal, gst, total: numericVal + gst };
    }
  };

  const purchasePriceInfo = calculateTaxValues(productData.purchasePrice);
  const retailPriceInfo = calculateTaxValues(productData.price);
  const gymPriceInfo = calculateTaxValues(productData.gymPrice);
  const wholesalePriceInfo = calculateTaxValues(productData.wholesalePrice);

  const salesPrice = parseFloat(productData.price) || 0;
  const basePrice = retailPriceInfo.base;
  const gstAmount = retailPriceInfo.gst;
  const totalPrice = retailPriceInfo.total;

  const servings = parseFloat(productData.servings) || 0;
  const dailyUsage = parseFloat(productData.dailyUsage) || 0;
  let estimatedDuration = "";
  if (servings > 0 && dailyUsage > 0) {
    const totalDays = servings / dailyUsage;
    if (totalDays >= 30) {
      const months = (totalDays / 30).toFixed(1);
      estimatedDuration = `${months} Month${months !== "1.0" ? "s" : ""}`;
    } else {
      estimatedDuration = `${Math.round(totalDays)} Day${Math.round(totalDays) !== 1 ? "s" : ""}`;
    }
  }

  const totalStock = normalizedProductData.warehouses.reduce(
    (sum, w) => sum + Number(w.quantity || 0),
    0
  );

  return (
    <form
      onSubmit={async (e) => {
        setIsSubmitting(true);
        await onSubmit(e);
        setIsSubmitting(false);
      }}
      className="bg-white rounded-none overflow-hidden w-full h-full flex flex-col"
    >
      {/* ================= HEADER ================= */}
      <div className="px-6 py-3 bg-[#172554] border-b flex justify-between items-center sticky top-0 z-10 text-white">
        <h2 className="text-lg font-semibold">
          {isEdit ? "Edit Product" : "Add New Product"}
        </h2>

        <button
          type="button"
          onClick={onClose}
          className="text-white hover:text-gray-200 text-xl"
        >
          ✕
        </button>
      </div>

      {/* ================= BODY (SCROLLABLE) ================= */}
      <div className="flex-1 overflow-y-auto p-6 rounded-none">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">

          {/* Product Name - Full Width */}
          <div className="md:col-span-2 flex flex-col gap-1">
            <label className={labelClass}>Product Name</label>
            <input
              type="text"
              className={inputClass}
              value={productData.name}
              onChange={(e) =>
                setProductData({ ...productData, name: e.target.value })
              }
            />
          </div>

          {/* Brand */}
          <div className="flex flex-col gap-1">
            <label className={labelClass}>Brand</label>
            <Select
              options={brands.map((b) => ({ value: b._id, label: b.name }))}
              value={brands.map((b) => ({ value: b._id, label: b.name })).find((opt) => opt.value === productData.brand) || null}
              onChange={(selected) =>
                setProductData({ ...productData, brand: selected ? selected.value : "" })
              }
              placeholder="Select Brand"
              isClearable
              className="text-sm"
              styles={{
                control: (base, state) => ({
                  ...base,
                  borderColor: state.isFocused ? '#60a5fa' : '#e2e8f0',
                  borderRadius: '0.5rem',
                  minHeight: '38px',
                  boxShadow: state.isFocused ? '0 0 0 2px #dbeafe' : 'none',
                  '&:hover': { borderColor: '#60a5fa' },
                }),
                menu: (base) => ({
                  ...base,
                  zIndex: 50,
                })
              }}
            />
          </div>

          {/* Category */}
          <div className="flex flex-col gap-1">
            <label className={labelClass}>Category</label>
            <Select
              options={categories.map((c) => ({ value: c._id, label: c.name }))}
              value={categories.map((c) => ({ value: c._id, label: c.name })).find((opt) => opt.value === productData.category) || null}
              onChange={(selected) =>
                setProductData({ ...productData, category: selected ? selected.value : "" })
              }
              placeholder="Select Category"
              isClearable
              className="text-sm"
              styles={{
                control: (base, state) => ({
                  ...base,
                  borderColor: state.isFocused ? '#60a5fa' : '#e2e8f0',
                  borderRadius: '0.5rem',
                  minHeight: '38px',
                  boxShadow: state.isFocused ? '0 0 0 2px #dbeafe' : 'none',
                  '&:hover': { borderColor: '#60a5fa' },
                }),
                menu: (base) => ({
                  ...base,
                  zIndex: 50,
                })
              }}
            />
          </div>

          {/* GST Rate (%) */}
          <div className="flex flex-col gap-1">
            <label className={labelClass}>GST Rate (%)</label>
            <div className="flex flex-wrap gap-2">
              <input
                type="number"
                placeholder="Custom %"
                className={`flex-[1.5] min-w-[80px] py-1.5 px-3 rounded-md text-sm transition-all border outline-none ${
                  ![5, 12, 18, 28].includes(Number(productData.taxRate)) && productData.taxRate !== "" && productData.taxRate != null
                    ? "border-[#172554] ring-1 ring-[#172554] text-[#172554] bg-slate-50"
                    : "border-slate-300 text-slate-700 focus:border-[#172554] focus:ring-1 focus:ring-slate-200"
                }`}
                value={
                  ![5, 12, 18, 28].includes(Number(productData.taxRate)) && productData.taxRate !== "" && productData.taxRate != null
                    ? productData.taxRate
                    : ""
                }
                onChange={(e) => setProductData({ ...productData, taxRate: e.target.value })}
              />
              {[5, 12, 18, 28].map((rate) => (
                <button
                  key={rate}
                  type="button"
                  onClick={() => setProductData({ ...productData, taxRate: rate })}
                  className={`flex-1 py-1.5 px-2 rounded-md text-sm font-medium transition-colors border ${
                    Number(productData.taxRate) === rate
                      ? "bg-[#172554] text-white border-[#172554] shadow-sm"
                      : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
                  }`}
                >
                  {rate}%
                </button>
              ))}
            </div>
          </div>

          {/* Tax Included */}
          <div className="flex flex-col gap-1">
            <label className={labelClass}>Tax Included</label>
            <div className="flex items-center gap-3 h-full">
              <input
                type="checkbox"
                id="taxInclude"
                checked={productData.taxInclude !== false}
                onChange={(e) =>
                  setProductData({ ...productData, taxInclude: e.target.checked })
                }
                className="w-5 h-5 rounded border-slate-300 text-blue-600 cursor-pointer"
              />
              <label htmlFor="taxInclude" className="text-sm text-slate-600 cursor-pointer">
                Tax is included in price
              </label>
            </div>
          </div>

          {/* 1. Purchase Price */}
          <div className="flex flex-col gap-1">
            <label className={labelClass}>Purchase Price (Cost)</label>
            <input
              type="number"
              className={inputClass}
              value={productData.purchasePrice}
              onChange={(e) =>
                setProductData({ ...productData, purchasePrice: e.target.value })
              }
            />
            {parseFloat(productData.purchasePrice) > 0 && (
              <span className="text-[11px] text-slate-500 font-medium mt-0.5 ml-1">
                {isTaxIncluded
                  ? `Base: ₹${purchasePriceInfo.base.toFixed(2)} | GST: ₹${purchasePriceInfo.gst.toFixed(2)}`
                  : `Final: ₹${purchasePriceInfo.total.toFixed(2)} (incl. GST)`}
              </span>
            )}
          </div>

          {/* 2. Retail Price */}
          <div className="flex flex-col gap-1">
            <label className={labelClass}>Retail Price</label>
            <input
              type="number"
              className={inputClass}
              value={productData.price}
              onChange={(e) =>
                setProductData({ ...productData, price: e.target.value })
              }
            />
            {parseFloat(productData.price) > 0 && (
              <span className="text-[11px] text-slate-500 font-medium mt-0.5 ml-1">
                {isTaxIncluded
                  ? `Base: ₹${retailPriceInfo.base.toFixed(2)} | GST: ₹${retailPriceInfo.gst.toFixed(2)}`
                  : `Final: ₹${retailPriceInfo.total.toFixed(2)} (incl. GST)`}
              </span>
            )}
          </div>

          {/* 3. Wholesale Shop Price */}
          <div className="flex flex-col gap-1">
            <label className={labelClass}>Wholesale Shop Price</label>
            <input
              type="number"
              className={inputClass}
              value={productData.wholesalePrice}
              onChange={(e) =>
                setProductData({ ...productData, wholesalePrice: e.target.value })
              }
            />
            {parseFloat(productData.wholesalePrice) > 0 && (
              <span className="text-[11px] text-slate-500 font-medium mt-0.5 ml-1">
                {isTaxIncluded
                  ? `Base: ₹${wholesalePriceInfo.base.toFixed(2)} | GST: ₹${wholesalePriceInfo.gst.toFixed(2)}`
                  : `Final: ₹${wholesalePriceInfo.total.toFixed(2)} (incl. GST)`}
              </span>
            )}
          </div>

          {/* 4. Gym Owner Price */}
          <div className="flex flex-col gap-1">
            <label className={labelClass}>Gym Owner Price</label>
            <input
              type="number"
              className={inputClass}
              value={productData.gymPrice}
              onChange={(e) =>
                setProductData({ ...productData, gymPrice: e.target.value })
              }
            />
            {parseFloat(productData.gymPrice) > 0 && (
              <span className="text-[11px] text-slate-500 font-medium mt-0.5 ml-1">
                {isTaxIncluded
                  ? `Base: ₹${gymPriceInfo.base.toFixed(2)} | GST: ₹${gymPriceInfo.gst.toFixed(2)}`
                  : `Final: ₹${gymPriceInfo.total.toFixed(2)} (incl. GST)`}
              </span>
            )}
          </div>

          {/* MRP */}
          <div className="flex flex-col gap-1">
            <label className={labelClass}>MRP</label>
            <input
              type="number"
              className={inputClass}
              value={productData.mrp}
              onChange={(e) =>
                setProductData({ ...productData, mrp: e.target.value })
              }
            />
          </div>

          {/* Empty spacer to align next elements properly */}
          <div className="hidden md:block" />

          {/* Pricing Summary Board - Spans 2 Columns */}
          {gstRate > 0 && (purchasePriceInfo.total > 0 || retailPriceInfo.total > 0 || wholesalePriceInfo.total > 0 || gymPriceInfo.total > 0) && (
            <div className="md:col-span-2 mt-2">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2.5">
                Pricing & GST Breakdown ({gstRate}% GST)
              </p>
              <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
                <ThemedTable className="text-left border-collapse w-full">
                  <thead>
                    <tr>
                      <th className="py-2">Price Tier</th>
                      <th className="py-2 text-right">Base (Excl. GST)</th>
                      <th className="py-2 text-right">GST Amount</th>
                      <th className="py-2 text-right">Final (Incl. GST)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {purchasePriceInfo.total > 0 && (
                      <tr>
                        <td className="py-2 font-semibold">Purchase Cost</td>
                        <td className="py-2 text-right">₹{purchasePriceInfo.base.toFixed(2)}</td>
                        <td className="py-2 text-right text-slate-400">₹{purchasePriceInfo.gst.toFixed(2)}</td>
                        <td className="py-2 text-right font-bold text-slate-900">₹{purchasePriceInfo.total.toFixed(2)}</td>
                      </tr>
                    )}
                    {retailPriceInfo.total > 0 && (
                      <tr>
                        <td className="py-2 font-semibold">Retail Price</td>
                        <td className="py-2 text-right">₹{retailPriceInfo.base.toFixed(2)}</td>
                        <td className="py-2 text-right text-slate-400">₹{retailPriceInfo.gst.toFixed(2)}</td>
                        <td className="py-2 text-right font-bold text-slate-900">₹{retailPriceInfo.total.toFixed(2)}</td>
                      </tr>
                    )}
                    {wholesalePriceInfo.total > 0 && (
                      <tr>
                        <td className="py-2 font-semibold">Wholesale Price</td>
                        <td className="py-2 text-right">₹{wholesalePriceInfo.base.toFixed(2)}</td>
                        <td className="py-2 text-right text-slate-400">₹{wholesalePriceInfo.gst.toFixed(2)}</td>
                        <td className="py-2 text-right font-bold text-slate-900">₹{wholesalePriceInfo.total.toFixed(2)}</td>
                      </tr>
                    )}
                    {gymPriceInfo.total > 0 && (
                      <tr>
                        <td className="py-2 font-semibold">Gym Price</td>
                        <td className="py-2 text-right">₹{gymPriceInfo.base.toFixed(2)}</td>
                        <td className="py-2 text-right text-slate-400">₹{gymPriceInfo.gst.toFixed(2)}</td>
                        <td className="py-2 text-right font-bold text-slate-900">₹{gymPriceInfo.total.toFixed(2)}</td>
                      </tr>
                    )}
                  </tbody>
                </ThemedTable>
              </div>
            </div>
          )}

          {/* Quantity Value */}
          <div className="flex flex-col gap-1">
            <label className={labelClass}>Quantity Value</label>
            <input
              type="number"
              placeholder="e.g. 100"
              className={inputClass}
              value={productData.quantityValue}
              onChange={(e) =>
                setProductData({ ...productData, quantityValue: e.target.value })
              }
            />
          </div>

          {/* Quantity Unit */}
          <div className="flex flex-col gap-1">
            <label className={labelClass}>Unit</label>
            <CreatableSelect
              isClearable
              options={units.map((u) => ({ value: u, label: u }))}
              value={productData.quantityUnit ? { value: productData.quantityUnit, label: productData.quantityUnit } : null}
              onChange={(selected) => {
                const value = selected ? selected.value : "";
                if (value && !units.includes(value)) setUnits([...units, value]);
                setProductData({ ...productData, quantityUnit: value });
              }}
              placeholder="e.g. kg, ltr, piece"
              className="text-sm"
              styles={{
                control: (base, state) => ({
                  ...base,
                  borderColor: state.isFocused ? '#60a5fa' : '#e2e8f0',
                  borderRadius: '0.5rem',
                  minHeight: '38px',
                  boxShadow: state.isFocused ? '0 0 0 2px #dbeafe' : 'none',
                  '&:hover': { borderColor: '#60a5fa' },
                }),
                menu: (base) => ({
                  ...base,
                  zIndex: 50,
                })
              }}
            />
          </div>

          {/* Rating */}
          <div className="flex flex-col gap-1">
            <label className={labelClass}>Rating</label>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <FaStar
                  key={star}
                  onClick={() =>
                    setProductData({ ...productData, rating: star })
                  }
                  className={`cursor-pointer text-xl ${
                    star <= (productData.rating || 0)
                      ? "text-yellow-400"
                      : "text-slate-300"
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Featured Product */}
          <div className="flex flex-col gap-1">
            <label className={labelClass}>Featured Product</label>
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="featured"
                checked={productData.featured || false}
                onChange={(e) =>
                  setProductData({ ...productData, featured: e.target.checked })
                }
                className="w-5 h-5 rounded border-slate-300 text-blue-600 cursor-pointer"
              />
              <label htmlFor="featured" className="text-sm text-slate-600 cursor-pointer">
                Mark as featured
              </label>
            </div>
          </div>

          {/* Expiry Date and Batch ID are now placed inside the Warehouse & Stock rows below */}

          {/* Supplement Usage Info */}
          <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-4 border-t border-slate-100 pt-4 mt-2">
            {/* Total Servings */}
            <div className="flex flex-col gap-1">
              <label className={labelClass}>Total Servings</label>
              <input
                type="number"
                placeholder="e.g. 30"
                className={inputClass}
                value={productData.servings || ""}
                onChange={(e) =>
                  setProductData({
                    ...productData,
                    servings: e.target.value,
                  })
                }
              />
            </div>

            {/* Daily Usage */}
            <div className="flex flex-col gap-1">
              <label className={labelClass}>Daily Usage</label>
              <input
                type="number"
                placeholder="e.g. 1"
                className={inputClass}
                value={productData.dailyUsage || ""}
                onChange={(e) =>
                  setProductData({
                    ...productData,
                    dailyUsage: e.target.value,
                  })
                }
              />
            </div>

            {/* Duration (Read-only) */}
            <div className="flex flex-col gap-1">
              <label className={labelClass}>Estimated Duration</label>
              <input
                type="text"
                disabled
                placeholder="—"
                className={`${inputClass} bg-slate-50 cursor-not-allowed font-medium text-blue-600`}
                value={estimatedDuration}
              />
            </div>
          </div>

          {/* Thumbnail Image - Full Width */}
          <div className="md:col-span-2 flex flex-col gap-1">
            <label className={labelClass}>Thumbnail Image</label>
            <input type="file" className={fileInputClass} onChange={handleThumbnail} />
            {thumbnailPreview && (
              <img
                src={thumbnailPreview}
                alt="thumbnail"
                className="w-28 h-28 mt-2 object-contain border rounded-md"
              />
            )}
          </div>

          {/* Description - Full Width */}
          <div className="md:col-span-2 flex flex-col gap-1">
            <label className={labelClass}>Description</label>
            <textarea
              rows={4}
              className="w-full rounded-lg border border-slate-200 p-3 text-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-400 outline-none transition"
              value={productData.description}
              onChange={(e) =>
                setProductData({ ...productData, description: e.target.value })
              }
            />
          </div>

          {/* Warehouses & Stock - Full Width */}
          <div className="md:col-span-2 flex flex-col gap-1">
            <div className="flex justify-between items-center bg-[#172554] text-white px-6 py-2.5 rounded-none -mx-6 mb-4 shadow-sm">
              <div className="flex items-center gap-2.5">
                <label className="text-sm font-semibold">Warehouses & Stock</label>
                <span className="bg-white/20 text-white text-[10px] font-bold px-2.5 py-0.5 rounded-full border border-white/10 shadow-sm uppercase tracking-wider">
                  Total: {totalStock}
                </span>
              </div>
              <button
                type="button"
                onClick={addWarehouse}
                className="px-3 py-1.5 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors text-xs font-medium w-auto"
              >
                Add Warehouse Stock
              </button>
            </div>

            {/* Column Headers */}
            <div className="grid grid-cols-12 gap-2 text-xs font-semibold text-slate-500 mb-1 px-1">
              <span className="col-span-3">Warehouse</span>
              <span className="col-span-2">Quantity</span>
              <span className="col-span-3">Expiry Date</span>
              <span className="col-span-3">Batch ID</span>
              <span className="col-span-1"></span>
            </div>

            {normalizedProductData.warehouses.map((wh, index) => (
              <div key={index} className="grid grid-cols-12 gap-2 mb-2">
                <select
                  className={`${inputClass} col-span-3`}
                  value={wh.locationId}
                  onChange={(e) => {
                    if (e.target.value === "__add_new__") {
                      setShowLocationModal(true);
                      return;
                    }
                    updateWarehouse(index, "locationId", e.target.value);
                  }}
                >
                  <option value="">Select Warehouse</option>
                  {locations.map((l) => (
                    <option key={l._id} value={l._id}>{l.name}</option>
                  ))}
                  <option value="__add_new__">➕ Add New Location</option>
                </select>

                <input
                  type="number"
                  placeholder="Qty"
                  className={`${inputClass} col-span-2`}
                  value={wh.quantity}
                  onChange={(e) =>
                    updateWarehouse(index, "quantity", Number(e.target.value))
                  }
                />

                <input
                  type="date"
                  placeholder="Expiry"
                  className={`${inputClass} col-span-3`}
                  value={wh.expiryDate || ""}
                  onChange={(e) =>
                    updateWarehouse(index, "expiryDate", e.target.value)
                  }
                />

                <input
                  type="text"
                  placeholder="Batch ID"
                  className={`${inputClass} col-span-3`}
                  value={wh.batchId || ""}
                  onChange={(e) =>
                    updateWarehouse(index, "batchId", e.target.value)
                  }
                />

                {normalizedProductData.warehouses.length > 1 ? (
                  <button
                    type="button"
                    onClick={() => removeWarehouse(index)}
                    className="col-span-1 flex items-center justify-center bg-red-50 text-red-500 rounded-lg hover:bg-red-100 hover:text-red-600 border border-red-100 transition shadow-sm h-10 w-full active:scale-95"
                    title="Remove warehouse stock row"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                ) : (
                  <div className="col-span-1 h-10" />
                )}
              </div>
            ))}
          </div>

          {/* Product Images - Full Width */}
          <div className="md:col-span-2 flex flex-col gap-1">
            <div className="flex justify-between items-center bg-[#172554] text-white px-6 py-2.5 rounded-none -mx-6 mb-4 shadow-sm">
              <div className="flex items-center gap-2.5">
                <label className="text-sm font-semibold">Product Images</label>
                <span className="bg-white/20 text-white text-[10px] font-bold px-2.5 py-0.5 rounded-full border border-white/10 shadow-sm uppercase tracking-wider">
                  {imagesPreview.length} / 4 Uploaded
                </span>
              </div>
              {imagesPreview.length < 4 && (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="px-3 py-1.5 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors text-xs font-medium w-auto"
                >
                  Choose Files
                </button>
              )}
            </div>

            <input
              type="file"
              ref={fileInputRef}
              accept="image/*"
              multiple
              onChange={handleImages}
              style={{ display: "none" }}
            />

            <div className="flex flex-wrap gap-4 mt-3">
              {imagesPreview.map((img, index) => (
                <div
                  key={index}
                  className="relative w-28 h-28 border border-slate-200 rounded-xl bg-gray-50 shadow-sm hover:shadow-md transition-shadow"
                >
                  <img
                    src={img}
                    alt={`product-${index}`}
                    className="w-full h-full object-cover rounded-xl"
                  />

                  <button
                    type="button"
                    onClick={() => removeImage(index)}
                    className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white 
                               w-6 h-6 rounded-full flex items-center 
                               justify-center text-xs border-2 border-white shadow-md
                               transition-all active:scale-90"
                    title="Remove image"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ================= FOOTER ================= */}
      <div className="px-6 py-3 border-t bg-white flex justify-end gap-3">
        <button
          type="button"
          onClick={onClose}
          className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold px-6 py-2 rounded-md transition-colors text-sm border border-slate-200"
        >
          Cancel
        </button>

        <button
          type="submit"
          disabled={isSubmitting}
          className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-6 py-2 rounded-md transition-colors text-sm flex items-center gap-2 disabled:opacity-60"
        >
          {isSubmitting ? (
            <Loader text={isEdit ? "Updating..." : "Saving..."} inline />
          ) : (
            isEdit ? "Update Product" : "Save Product"
          )}
        </button>

      </div>
    </form>
  );
};

export default AddProducts;
